import {useState, useRef, useCallback, useEffect} from 'react'

const RESAMPLING_WORKLET_PATH = '/resamplingAudioWorklet.js'
const PCM_WORKLET_PATH = '/pcmAudioWorklet.js'
const VOLUME_METER_WORKLET_PATH = '/volumeMeterWorklet.js'

export function useAudioInputForGemini() {
  const isRecordingRef = useRef<boolean>(false)
  const [numericalVolume, setNumericalVolume] = useState<number>(0)
  const audioContextRef = useRef<AudioContext | null>(null)
  const mediaStreamRef = useRef<MediaStream | null>(null)
  const mediaStreamSourceRef = useRef<MediaStreamAudioSourceNode | null>(null)
  const resamplingNodeRef = useRef<AudioWorkletNode | null>(null)
  const pcmNodeRef = useRef<AudioWorkletNode | null>(null)
  const volumeMeterNodeRef = useRef<AudioWorkletNode | null>(null)

  // Track which worklets have been loaded for this AudioContext
  const loadedWorkletsRef = useRef<Set<string>>(new Set())

  const onChunkRef = useRef<
    (chunk: ArrayBuffer, volumeDetected: boolean) => void
  >(() => {})
  const onErrorRef = useRef<(error: string) => void>(() => {})

  const targetSampleRate = 16000

  const stopAudioProcessingNodes = useCallback(() => {
    // console.log('[useAudioInputForGemini] Stopping audio processing nodes...')
    if (pcmNodeRef.current) {
      try {
        pcmNodeRef.current.port.onmessage = null
        pcmNodeRef.current.disconnect()
      } catch (e) {
        console.warn(
          '[useAudioInputForGemini] Error disconnecting PCM node:',
          e
        )
      }
      pcmNodeRef.current = null
    }
    if (volumeMeterNodeRef.current) {
      try {
        volumeMeterNodeRef.current.port.onmessage = null
        volumeMeterNodeRef.current.disconnect()
      } catch (e) {
        console.warn(
          '[useAudioInputForGemini] Error disconnecting Volume Meter node:',
          e
        )
      }
      volumeMeterNodeRef.current = null
    }
    if (resamplingNodeRef.current) {
      try {
        resamplingNodeRef.current.disconnect()
      } catch (e) {
        console.warn(
          '[useAudioInputForGemini] Error disconnecting resampling node:',
          e
        )
      }
      resamplingNodeRef.current = null
    }
    if (mediaStreamSourceRef.current) {
      try {
        mediaStreamSourceRef.current.disconnect()
      } catch (e) {
        console.warn(
          '[useAudioInputForGemini] Error disconnecting media stream source:',
          e
        )
      }
      mediaStreamSourceRef.current = null
    }
    // Don't stop MediaStream tracks here - they're managed by Chat.tsx
    // if (mediaStreamRef.current) {
    //   console.log('[useAudioInputForGemini] Stopping media stream tracks...');
    //   mediaStreamRef.current.getTracks().forEach((track) => track.stop())
    //   mediaStreamRef.current = null
    // }
    mediaStreamRef.current = null
    // audioContextRef is managed by the parent (Chat.tsx)
  }, [])

  const startRecording = useCallback(
    async (
      context: AudioContext, // Expect an initialized and active AudioContext from parent
      stream: MediaStream, // Expect an active MediaStream from parent
      onAudioChunk: (chunk: ArrayBuffer, volumeDetected: boolean) => void,
      onRecordingError: (error: string) => void
    ) => {
      if (isRecordingRef.current) {
        //console.warn('[useAudioInputForGemini] Already recording.bce')
        return
      }
      //console.log('[useAudioInputForGemini] Starting recording...bce')

      onChunkRef.current = onAudioChunk
      onErrorRef.current = onRecordingError

      // If AudioContext changed, reset worklet tracking
      if (audioContextRef.current !== context) {
        loadedWorkletsRef.current.clear()
      }
      audioContextRef.current = context
      mediaStreamRef.current = stream

      try {
        if (
          audioContextRef.current.state === 'suspended' ||
          audioContextRef.current.state === 'closed'
        ) {
          // console.log('[useAudioInputForGemini] Resuming AudioContext...');
          await audioContextRef.current.resume()
          if (
            audioContextRef.current.state === 'suspended' ||
            audioContextRef.current.state === 'closed'
          ) {
            throw new Error('AudioContext could not be resumed.')
          }
        }
        // console.log('[useAudioInputForGemini] AudioContext sample rate:', audioContextRef.current.sampleRate);

        // 1. Create MediaStreamSource from the provided stream
        mediaStreamSourceRef.current = audioContextRef.current.createMediaStreamSource(
          mediaStreamRef.current
        )

        // 2. Load and create Resampling Worklet Node
        const resamplingWorkletKey = `resampling-${audioContextRef.current.sampleRate}`
        if (!loadedWorkletsRef.current.has(resamplingWorkletKey)) {
          try {
            await audioContextRef.current.audioWorklet.addModule(
              RESAMPLING_WORKLET_PATH
            )
            loadedWorkletsRef.current.add(resamplingWorkletKey)
          } catch (e) {
            // If worklet is already loaded, this might throw but we can continue
            if (!(e as Error).message.includes('already exists')) {
              throw new Error(
                `Failed to load resampling worklet: ${(e as Error).message}`
              )
            }
            loadedWorkletsRef.current.add(resamplingWorkletKey)
          }
        }
        resamplingNodeRef.current = new AudioWorkletNode(
          audioContextRef.current,
          'resampling-processor',
          {
            processorOptions: {
              inputSampleRate: audioContextRef.current.sampleRate,
              outputSampleRate: targetSampleRate, // 16000 Hz
            },
          }
        )
        resamplingNodeRef.current.onprocessorerror = (event) => {
          console.error(
            '[useAudioInputForGemini] ResamplingProcessor error:',
            event
          )
          onErrorRef.current('Resampling processor error.')
          stopAudioProcessingNodes()
          isRecordingRef.current = false
        }

        // 3. Load and create PCM Worklet Node
        const pcmWorkletKey = 'pcm-audio-processor'
        if (!loadedWorkletsRef.current.has(pcmWorkletKey)) {
          try {
            await audioContextRef.current.audioWorklet.addModule(
              PCM_WORKLET_PATH
            )
            loadedWorkletsRef.current.add(pcmWorkletKey)
          } catch (e) {
            // If worklet is already loaded, this might throw but we can continue
            if (!(e as Error).message.includes('already exists')) {
              throw new Error(
                `Failed to load PCM worklet: ${(e as Error).message}`
              )
            }
            loadedWorkletsRef.current.add(pcmWorkletKey)
          }
        }
        pcmNodeRef.current = new AudioWorkletNode(
          audioContextRef.current,
          'pcm-audio-processor'
        )
        pcmNodeRef.current.onprocessorerror = (event) => {
          console.error(
            '[useAudioInputForGemini] PCMAudioProcessor error:',
            event
          )
          onErrorRef.current('PCM processor error.')
          stopAudioProcessingNodes()
          isRecordingRef.current = false
        }

        // 4. Setup PCM Worklet message handler
        pcmNodeRef.current.port.onmessage = (event: MessageEvent) => {
          if (
            event.data.type === 'audioData' &&
            event.data.buffer instanceof ArrayBuffer
          ) {
            onChunkRef.current(event.data.buffer, false)
          } else if (event.data.type === 'error') {
            console.error(
              '[useAudioInputForGemini] Error from PCMAudioProcessor worklet:',
              event.data.message
            )
            onErrorRef.current(event.data.message)
          }
        }

        // 5. Load and create Volume Meter Worklet Node
        const volumeWorkletKey = 'volume-meter-processor'
        if (!loadedWorkletsRef.current.has(volumeWorkletKey)) {
          try {
            await audioContextRef.current.audioWorklet.addModule(
              VOLUME_METER_WORKLET_PATH
            )
            loadedWorkletsRef.current.add(volumeWorkletKey)
          } catch (e) {
            // If worklet is already loaded, this might throw but we can continue
            if (!(e as Error).message.includes('already exists')) {
              throw new Error(
                `Failed to load Volume Meter worklet: ${(e as Error).message}`
              )
            }
            loadedWorkletsRef.current.add(volumeWorkletKey)
          }
        }
        volumeMeterNodeRef.current = new AudioWorkletNode(
          audioContextRef.current,
          'volume-meter-processor',
          {processorOptions: {updateIntervalInMS: 50}}
        )
        volumeMeterNodeRef.current.onprocessorerror = (event) => {
          console.error(
            '[useAudioInputForGemini] VolumeMeterProcessor error:',
            event
          )
        }
        volumeMeterNodeRef.current.port.onmessage = (event: MessageEvent) => {
          if (
            event.data.type === 'volumeUpdate' &&
            typeof event.data.volume === 'number'
          ) {
            setNumericalVolume(event.data.volume)
          }
        }

        // 6. Connect the audio graph:
        mediaStreamSourceRef.current.connect(resamplingNodeRef.current)
        resamplingNodeRef.current.connect(pcmNodeRef.current)
        resamplingNodeRef.current.connect(volumeMeterNodeRef.current)

        isRecordingRef.current = true
        // console.log('[useAudioInputForGemini] Recording started successfully.');
      } catch (error) {
        console.error(
          '[useAudioInputForGemini] Error starting recording:',
          error
        )
        onErrorRef.current(
          error instanceof Error
            ? error.message
            : 'An unknown error occurred during recording setup.'
        )
        stopAudioProcessingNodes() // Clean up on error
        isRecordingRef.current = false
      }
    },
    [targetSampleRate, stopAudioProcessingNodes] // Removed isRecording dependency since it's now a ref
  )

  const stopRecording = useCallback(() => {
    if (!isRecordingRef.current) {
      /*console.log(
        '[useAudioInputForGemini] Not recording, no action to stop.abc'
      )*/
      return
    }
    // console.log('[useAudioInputForGemini] Stopping recording...abc')
    stopAudioProcessingNodes()
    isRecordingRef.current = false
    setNumericalVolume(0)
    // console.log('[useAudioInputForGemini] Recording stopped.abc')
  }, [stopAudioProcessingNodes]) // Removed isRecording dependency since it's now a ref

  // Cleanup effect for component unmount
  // eslint-disable-next-line arrow-body-style
  useEffect(() => {
    // eslint-disable-next-line arrow-body-style
    return () => {
      // console.log('[useAudioInputForGemini] Unmounting, ensuring cleanup...');
      if (isRecordingRef.current) {
        stopRecording() // This will call stopAudioProcessingNodes
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stopRecording]) // Removed isRecording dependency since it's now a ref

  // Getter function for current recording state
  const getIsRecording = useCallback(() => isRecordingRef.current, [])

  return {
    numericalVolume,
    startRecording,
    stopRecording,
    getIsRecording, // Expose getter function
  }
}
